/* eslint-disable @typescript-eslint/indent */
/* eslint-disable no-async-promise-executor */
import Currency from '@components/common/Currrency/Currency';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { isProductUnAvailable } from '@helpers/product';
import { sumBy } from 'lodash';
import { useCookies } from 'react-cookie';
import { GuestGroupType, ProductDetailsProps } from '@customTypes/product-details';
import useAddOns from '@hooks/useAddons';
import { addonSelected, addToCart as addToCartEvent, productQuantitySelected } from '@events/DLP';
import actionTypes from '@context/actionTypes';
import { store } from '@context/store';
import useAddToCart from '@hooks/useAddToCart';
import toggleErrorPopup from '@helpers/toggleErrorPopup';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import User from '@customTypes/user';
import { v4 as uuidv4 } from 'uuid';
import useCounterValidation from '@hooks/useCounterValidation';
import Addons from '@components/common/Addons';
import useAlgoliaEvents from '@hooks/useAlgoliaEvents';
import useHotSpot from '@hooks/useHotSpot';
import { FAIR_PRICING_VARIANT } from '@constants/fairPricing';
import { getProductPriceForGuestType } from '@utils/fairPricing';
import ImageLoaded from '@components/common/ImageLoaded/ImageLoaded';
import SpaBanner from '@components/ProductDetails/SpaBanner';
import { TagIcon as TagIconSolar } from '@resortpass/solar-icons';
import { useExperiment } from '@context/ExperimentContext';
import { hotelOffersTags } from '@constants/hotelOffers';
import { Separator } from '@solar/common/Separator';
import { FreeCancellationTextFromDate } from '@components/FreeCancellation';
import { isTreatment } from '@helpers/experiment';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import GuestSelectorDrawer from './GuestSelectorDrawer';
import ProductDescription from './ProductDescription';
import ProductDetailsFooter from './ProductDetailsFooter';
import CounterVariant from './CounterVariant';
import FormErrorText from './FormErrorText';
import GuestGroup from './GuestGroup';

function GroupedBilledProductDetails({
  cartData,
  date,
  hotelDetails,
  product,
  setCartData,
  setIsDrawerOpen,
  showFairPricing,
}: ProductDetailsProps) {
  const { dispatch, state } = useContext(store);
  const { guestsGroupBag } = state;

  const MaxGuestsGroupBag = [
    {
      adults: product.max_adults_occupancy,
      children:
        product.max_person_occupancy !== product.max_adults_occupancy
          ? product.max_children_occupancy
          : 0,
      id: uuidv4(),
    },
  ];

  const [tempBag, setTempBag] = useState<GuestGroupType[]>(MaxGuestsGroupBag);
  const [numGuestGroups, setNumGuestGroups] = useState<number>(1);
  const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState<boolean>(false);
  const { freeCancellationExperiment } = useFeatureFlagContext();

  const [cookies] = useCookies();
  const { getItem } = useSessionStorage();
  const { algoliaAddedToCartEvent } = useAlgoliaEvents();
  const [addToCart] = useAddToCart();

  const user: User = cookies.userInformation;
  const sessionID = getItem(sessionStorageKeys.SESSION_ID);

  const adultsSum = useMemo(() => sumBy(guestsGroupBag, 'adults'), [guestsGroupBag]);

  const errorRef = useRef<HTMLDivElement>(null);

  const { addOnItems, resetAddOns, updateAddonItem, onAddonUnitsSelect, showAddons } = useAddOns(
    product,
    hotelDetails,
    addonSelected,
    adultsSum,
  );
  const { counterValidation, errorMessage, showErrorMessage } = useCounterValidation(
    0,
    0,
    cartData,
    product,
    true,
  );
  const { applyHotSpotDiscountToTotal, isDateWithinVisitWindow } = useHotSpot();

  const resetCounters = () => {
    dispatch({
      type: actionTypes.SET_GUEST_GROUPS_BAG,
      payload: [],
    });
  };

  const handleFooterButtonClick = async () =>
    new Promise(async (resolve, reject) => {
      if (date && guestsGroupBag.length) {
        try {
          const { cartItems, updatedCartData } = await addToCart(
            addOnItems,
            0,
            0,
            date,
            cartData,
            product,
            hotelDetails,
            dispatch,
            user,
            guestsGroupBag,
          );

          if (updatedCartData) {
            setCartData(updatedCartData);
          }

          // Fire events
          if (cartItems) {
            cartItems.items.forEach((item) => {
              addToCartEvent(
                product,
                hotelDetails,
                addOnItems,
                item.no_of_adults,
                item.no_of_childs,
                updatedCartData,
                date,
              );
            });
          }

          algoliaAddedToCartEvent(hotelDetails, user, sessionID);

          // Reset to defaults.
          setIsDrawerOpen(false);
          resetCounters();
          resetAddOns();
          resolve(updatedCartData);
        } catch (error) {
          toggleErrorPopup(error, dispatch);
          reject(error);
        }
      }
    });

  const handleOnCounterChange = (value: number) => {
    if (guestsGroupBag.length === 0) {
      const initialGuestsGroupBag = new Array(value).fill(0).map(() => ({
        adults: product.max_adults_occupancy,
        children: 0,
        id: uuidv4(),
      }));

      setNumGuestGroups(initialGuestsGroupBag.length);
      setTempBag(initialGuestsGroupBag);
    } else {
      const updatedBag = [...guestsGroupBag];

      if (guestsGroupBag.length > value) {
        // Reducing: Remove the last item in the array.
        updatedBag.pop();
      } else {
        // Adding the difference to the global state bag.
        for (let i = 0; i < value - guestsGroupBag.length; i += 1) {
          updatedBag.push({
            adults: product.max_adults_occupancy,
            children: 0,
            id: uuidv4(),
          });
        }
      }

      dispatch({
        type: actionTypes.SET_GUEST_GROUPS_BAG,
        payload: updatedBag,
      });
      setTempBag(updatedBag);
    }
  };

  useEffect(() => {
    if (guestsGroupBag && guestsGroupBag.length) {
      setNumGuestGroups(guestsGroupBag.length);
    }
  }, [guestsGroupBag]);

  // Reset Counters on unmount
  useEffect(
    () => () => {
      resetCounters();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    dispatch({
      type: actionTypes.SET_GUEST_GROUPS_BAG,
      payload: tempBag,
    });
    // Event
    productQuantitySelected(product, hotelDetails);
  }, [dispatch, hotelDetails, product, tempBag]);

  useEffect(() => {
    if (showErrorMessage && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showErrorMessage]);

  const { hotelOffersExperiment } = useExperiment();
  const showHotelOffers = hotelOffersExperiment === 'on';

  const hasProductOffer = !!product.product_offer && showHotelOffers;

  const hotelOfferTag = hotelOffersTags[product.product_offer!];

  return (
    <>
      <div className="w-full">
        <div className="h-250px w-full relative">
          <ImageLoaded url={product.photo_url.url} alt="product" layout="fill" />
        </div>

        <div className="w-full px-4 pt-5 pb-5">
          <div className="flex flex-col border-b border-rp-gray-divider pb-6">
            <div className="flex flex-row items-center justify-between">
              <div className="flex flex-col">
                <p className="font-rp-pn-semi-bold leading-6 text-18">Quantity</p>

                <div className="flex font-rp-pn-regular items-center text-custom-black">
                  {!date && <p className="leading-22px mr-1 text-sm">Starting at</p>}

                  {date &&
                  product.is_hot_spot &&
                  hotelDetails.hot_spot &&
                  isDateWithinVisitWindow(date, hotelDetails) ? (
                    <div className={`${date && product.is_hot_spot ? 'mr-2' : ''}`}>
                      <Currency
                        value={applyHotSpotDiscountToTotal(
                          +product.adult_price,
                          hotelDetails.hot_spot,
                          showFairPricing,
                        )}
                        valueClasses="leading-4 text-solar-brand text-md"
                      />
                    </div>
                  ) : null}
                  <Currency
                    value={getProductPriceForGuestType(product, 'adult', showFairPricing)}
                    valueClasses={`leading-4 ${
                      date && product.is_hot_spot && isDateWithinVisitWindow(date, hotelDetails)
                        ? 'text-rp-disabled text-base line-through'
                        : 'text-custom-black text-md'
                    }`}
                  />
                </div>

                {!(date && isProductUnAvailable(product, cartData, hotelDetails, date)) &&
                showFairPricing ? (
                  <p className="text-sm text-rp-gray whitespace-nowrap d:-mt-1 d:mb-1">
                    {FAIR_PRICING_VARIANT === 'variant1'
                      ? 'Includes fees'
                      : `+$${(+getProductPriceForGuestType(
                          product,
                          'adult',
                          showFairPricing,
                        )).toFixed(0)} in fees`}
                  </p>
                ) : null}
              </div>

              <div className="flex items-center space-x-4">
                <CounterVariant
                  dispatch={dispatch}
                  counter={numGuestGroups}
                  setParentCounter={setNumGuestGroups}
                  productData={product}
                  date={date}
                  personType="adults"
                  isDisabled={date && isProductUnAvailable(product, cartData, hotelDetails, date)}
                  onChange={handleOnCounterChange}
                  validate={counterValidation}
                />
              </div>
            </div>

            <p className="mt-6 font-rp-pn-semi-bold leading-6 text-18 pt-6 border-t border-rp-gray-divider">
              Guests
            </p>
            {date &&
              numGuestGroups > 0 &&
              !isProductUnAvailable(product, cartData, hotelDetails, date) && (
                <div className="pl-1">
                  {tempBag.map((group, index, { length }) => (
                    <GuestGroup
                      cartData={cartData}
                      date={date}
                      group={group}
                      index={index}
                      isLastItem={index + 1 !== length}
                      key={group.id}
                      product={product}
                      setIsOpen={setIsBottomDrawerOpen}
                      tempBag={tempBag}
                      setTempBag={setTempBag}
                      hideHeader
                    />
                  ))}
                </div>
              )}

            {/* Error message */}
            {showErrorMessage && (
              <div className="mt-6" ref={errorRef}>
                <FormErrorText showInfoIcon text={errorMessage} />
              </div>
            )}
          </div>
          {product.cancellation_date && isTreatment(freeCancellationExperiment) && (
            <div className="flex flex-col py-6 gap-6">
              <FreeCancellationTextFromDate dateString={product.cancellation_date} />
              <Separator className="h-px" />
            </div>
          )}

          {/* Addons */}
          <Addons
            addons={addOnItems}
            onAddonUnitsSelect={onAddonUnitsSelect}
            showAddons={showAddons}
            updateAddonItem={updateAddonItem}
          />

          <SpaBanner product={product} />

          {hasProductOffer ? (
            <div className="flex flex-col border-b border-rp-gray-divider pb-6 mt-6">
              <div className="py-1 flex flex-row gap-1 items-center">
                <TagIconSolar width={16} height={16} color="#3164E9" />
                <span className="text-sm text-solar-brand font-rp-basetica leading-22px">
                  {hotelOfferTag?.title.replace('[[AMOUNT]]', product.product_offer_amount || '')}
                </span>
              </div>
              <span className="font-rp-basetica text-sm">
                {hotelOfferTag?.description.replace(
                  '[[AMOUNT]]',
                  product.product_offer_amount || '',
                )}
              </span>
            </div>
          ) : null}

          {/* Product description */}
          {product.description && <ProductDescription product={product} />}
        </div>

        <div className="mt-16">
          <ProductDetailsFooter
            adultCounter={0}
            date={date}
            cartData={cartData}
            hotelDetails={hotelDetails}
            onButtonClick={handleFooterButtonClick}
            product={product}
            guestsGroupBag={guestsGroupBag}
          />
        </div>
      </div>

      {isBottomDrawerOpen && (
        <GuestSelectorDrawer
          cartData={cartData}
          date={date}
          hotelDetails={hotelDetails}
          isOpen={isBottomDrawerOpen}
          product={product}
          setIsOpen={setIsBottomDrawerOpen}
          setTempBag={setTempBag}
          tempBag={tempBag}
        />
      )}
    </>
  );
}

export default GroupedBilledProductDetails;
